// @ts-nocheck
import * as React from 'react';
import styled from 'styled-components';
import * as P from 'polished';
import { pipe } from 'fp-ts/lib/function';
import * as A from 'fp-ts/lib/Array';
import * as O from 'fp-ts/lib/Option';

import * as Theme from '../../../serviceTheme';
import { useIORef } from '../../../core/hooks';
import { withConfig } from '../../utils';
import { InputColors, InputConfig } from '../../Input';
import { Layout, Entry, slot, useEffect, useSetter, useGetter, InnerData, Data } from './slots';

// -------------------------------------------------------------------------------------
// Registry
// -------------------------------------------------------------------------------------
declare module '../../Controls/hkt' {
    interface URIControlsToKind {
        'group-buttons-multi': Props
    }
}

// -------------------------------------------------------------------------------------
// Model
// -------------------------------------------------------------------------------------

type Props = {
    name: string,
    data: Data,
    value: string[],
    onChange: (x: Data, name: string) => void,
    className?: string,
    disabled?: boolean,
    children?: React.ReactNode,
}

type Ref = { innerRef: ((instance: HTMLDivElement | null) => void) | React.MutableRefObject<HTMLDivElement | null> | null };

// -------------------------------------------------------------------------------------
// Slots
// -------------------------------------------------------------------------------------

export const Item = slot('item')(({ children, index, className }) => {

    const main = useGetter('main');
    const cursor = useGetter('cursor');

    const handleClick = () => {
        main.modifyValue(x => pipe(
            x, 
            A.modifyAt(index, (x) => {
                return { 
                    ...x, 
                    checked: !x.checked,
                }
            }),
            O.fold(
                () => x,
                (xs) => xs,
            ),
        ));

        cursor.set(index);
    }


    const x = main.getData()[index];

    return (
        <ItemS 
            onClick={handleClick}
            isActive={x.checked}
            className={className}>
            {children(x as any)}
        </ItemS>
    );
});


const ItemS = styled.button<{ isActive: boolean }>`
    padding-left: .6em;
    padding-right: .6em;
    margin: 0 0.15em;
    color: ${({ isActive }) => isActive ? '#fff' : '#1D2124'};
    background-color: ${({ isActive, theme }) => isActive ? Theme.safeGetPrimary({ theme }) : '#fff'};
    border-radius: 2px;
    box-shadow: ${({ isActive }) => isActive ? '0px 4px 4px rgba(46, 130, 139, 0.19)' : 'none'};
    border: 1px solid;
    border-color: transparent;

    &:hover {
        ${({ isActive, theme }) => isActive ? ({}) : ({
            borderColor: Theme.safeGetPrimary({ theme }),
            backgroundColor: P.transparentize(0.8, Theme.safeGetPrimary({ theme })),
        })}
    }
`;



export const Inner = slot('inner')(({ className }) => {

    const main = useGetter('main');

    return (
        <InnerS className={className}>
            {main.getData().map((_, index) => {
                return (
                    <Layout 
                        key={index}
                        name="item"
                        index={index}
                    />
                );
            })}
        </InnerS>
    );
});


const InnerS = styled.div`
    display: flex;
    margin: 0 -0.15em;
    height: 100%;
`;


// -------------------------------------------------------------------------------------
// Host
// -------------------------------------------------------------------------------------


export const Group = React.forwardRef<HTMLDivElement, Props>(({ children, ...props }, ref) => {
    return (
        <Entry connectLayout={children}>
            <Cursor />
            <Core innerRef={ref} {...props} />
        </Entry>
    );
});


// -------------------------------------------------------------------------------------
// Core
// -------------------------------------------------------------------------------------




const getValue = (d: InnerData) => {
    console.log('!');
    return d.reduce((acc, curr) => {
        const { checked, ...rest } = curr;
        return acc.concat(checked ? [rest] : []);
    }, [] as InnerData);
}

const Core: React.FunctionComponent<Props & Ref> = ({
    name, 
    data, 
    value, 
    onChange, 
    className, 
    disabled = false,
}) => {

    const preparedValue = React.useMemo(() => pipe(
        value,
        O.fromNullable,
        O.fold(
            () => [],
            (r) => r,
        ),
    ), [value]);

    const preparedData = React.useMemo(() => data.map((d, i) => {
        return  { 
            ...d, 
            checked: preparedValue.includes(d.value),
        }
    }), [data]);

    const { read: getCurrentValue, write: _changeValue } = useIORef(getValue(preparedData));
    const { read: getData, write: changeData, modify: modifyData } = useIORef(preparedData);


    React.useEffect(() => {
        _changeValue(getValue(preparedValue))();
        changeData(preparedData)();
    }, [preparedData]);

    const [, runChangeValue] = useEffect('changeValue');


    const modifyValue = runChangeValue((f) => {
        modifyData(f)();
        onChange(getValue(getData()).map(x => x.value), name);
    });

    useSetter('main', {
        name,
        getData,
        getCurrentValue,
        modifyValue,
    });

    return (
        <Container 
            className={className}
            disabled={disabled}
            tabIndex={0}>
            <Layout name="inner" />
        </Container>
    );
}


const Container_ = styled.div`
    padding: .3em;

    ${InputColors.rest}
    ${InputColors.normal}

    width: max-content;

    &[value=""] {
        ${InputColors.emptyValue}
    }

    &:disabled {
        ${InputColors.disabled}

        &[value=""] {
            ${InputColors.disabledEmptyValue}
        }
    }

    &:focus {
        ${InputColors.focus}
    }

    &:not(:focus):not(:disabled):hover {
        ${InputColors.hover}

        &[value=""] {
            ${InputColors.hoverEmptyValue}
        }
    }

    &::placeholder {
        color: #898f9e;
    }
`;


export const Container = withConfig<any, HTMLDivElement>()(InputConfig)(Container_);


const Cursor: React.FunctionComponent = () => {

    const { 
        read: get, 
        write: _setCursor,
        modify: _modify
    } = useIORef(0);

    const [, runCursorMove] = useEffect('cursorMove');

    const set = runCursorMove((n: number) => {
        _setCursor(n)();
    });

    const modify = runCursorMove((f: (x: number) => number) => {
        _modify(f)();
    })


    useSetter('cursor', {
        get,
        set,
        modify,
    });

    return null;
}
